import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Title,
} from "@mantine/core";

import {
  previewBrandingEmail,
  previewBrandingPage,
  previewBrandingPdf,
  type BrandingFields,
  type EmailPreview,
  type EmailPreviewMessage,
} from "@/api/branding";
import { t, type MessageKey } from "@/i18n";

/**
 * Live preview of the draft before it is saved (B3): the patient page and a sample PDF,
 * both rendered by report-summary from its fixed synthetic report through the preview
 * route, and the patient email, rendered by Smart Reports with the code that sends it
 * (contract 4.9, B7). A lab's wording goes live on save (D12), so the email shown is the
 * real one, never a copy worked out here.
 *
 * **The page and the email are HTML carrying lab-controlled text, so each is shown only in
 * a fully sandboxed iframe** (no script, no same-origin access), on top of the
 * `Content-Security-Policy: sandbox` the server already sends them with. The PDF opens in a
 * new tab from an object URL, as the lab's own attachments do. Nothing is stored by any.
 */

const PAGE_DEBOUNCE_MS = 900;

interface PreviewPanelProps {
  organizationId: string;
  fields: BrandingFields;
  /** The languages the email can be previewed in, English and Arabic first. */
  emailLanguages: string[];
}

export function PreviewPanel({
  organizationId,
  fields,
  emailLanguages,
}: PreviewPanelProps): JSX.Element {
  const [tab, setTab] = useState<string | null>("page");
  return (
    <Card withBorder data-testid="branding-preview">
      <Title order={3} mb="xs">
        {t("branding.preview_title")}
      </Title>
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="page">{t("branding.preview_page")}</Tabs.Tab>
          <Tabs.Tab value="pdf">{t("branding.preview_pdf")}</Tabs.Tab>
          <Tabs.Tab value="email">{t("branding.preview_email")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="page" pt="sm">
          {tab === "page" && <PagePreview organizationId={organizationId} fields={fields} />}
        </Tabs.Panel>
        <Tabs.Panel value="pdf" pt="sm">
          <PdfPreview organizationId={organizationId} fields={fields} />
        </Tabs.Panel>
        <Tabs.Panel value="email" pt="sm">
          {tab === "email" && (
            <EmailPreviewView
              organizationId={organizationId}
              fields={fields}
              languages={emailLanguages}
            />
          )}
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
}

function LanguagePicker({
  value,
  onChange,
}: {
  value: "en" | "ar";
  onChange: (v: "en" | "ar") => void;
}): JSX.Element {
  return (
    <SegmentedControl
      size="xs"
      value={value}
      onChange={(v) => onChange(v as "en" | "ar")}
      data={[
        { value: "en", label: t("branding.preview_english") },
        { value: "ar", label: t("branding.preview_arabic") },
      ]}
    />
  );
}

function PagePreview({
  organizationId,
  fields,
}: {
  organizationId: string;
  fields: BrandingFields;
}): JSX.Element {
  const [language, setLanguage] = useState<"en" | "ar">("en");
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const serial = useRef(0);
  const draftKey = JSON.stringify(fields);

  useEffect(() => {
    const mine = ++serial.current;
    const timer = window.setTimeout(() => {
      setLoading(true);
      previewBrandingPage(organizationId, language, fields)
        .then((text) => {
          if (mine !== serial.current) return;
          setHtml(text);
          setError(null);
        })
        .catch((err: unknown) => {
          if (mine !== serial.current) return;
          setError(err instanceof Error ? err.message : t("branding.preview_failed"));
        })
        .finally(() => {
          if (mine === serial.current) setLoading(false);
        });
    }, PAGE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // `fields` is compared through its JSON so a re-render with the same draft does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, language, draftKey]);

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <LanguagePicker value={language} onChange={setLanguage} />
        <Text size="xs" c="dimmed">
          {loading ? t("branding.preview_loading") : t("branding.preview_live_hint")}
        </Text>
      </Group>
      {error !== null && (
        <Alert color="orange" p="xs" data-testid="branding-preview-error">
          {error}
        </Alert>
      )}
      {html !== null && (
        <iframe
          title={t("branding.preview_page")}
          sandbox=""
          srcDoc={html}
          style={{ width: "100%", height: 560, border: "1px solid #ddd", background: "#fff" }}
          data-testid="branding-preview-frame"
        />
      )}
    </Stack>
  );
}

function PdfPreview({
  organizationId,
  fields,
}: {
  organizationId: string;
  fields: BrandingFields;
}): JSX.Element {
  const [language, setLanguage] = useState<"en" | "ar">("en");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function open(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const blob = await previewBrandingPdf(organizationId, language, fields);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.rel = "noopener";
      link.target = "_blank";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("branding.preview_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="xs">
      <Group>
        <LanguagePicker value={language} onChange={setLanguage} />
        <Button onClick={() => void open()} loading={busy} data-testid="branding-preview-pdf">
          {t("branding.preview_pdf_open")}
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        {t("branding.preview_pdf_hint")}
      </Text>
      {error !== null && (
        <Alert color="orange" p="xs" data-testid="branding-preview-pdf-error">
          {error}
        </Alert>
      )}
    </Stack>
  );
}

const MESSAGES: { value: EmailPreviewMessage; label: MessageKey }[] = [
  { value: "report_ready", label: "branding.wording_report_ready" },
  { value: "report_updated", label: "branding.wording_report_updated" },
  { value: "pin_reissued", label: "branding.wording_pin_reissued" },
];

function EmailPreviewView({
  organizationId,
  fields,
  languages,
}: {
  organizationId: string;
  fields: BrandingFields;
  languages: string[];
}): JSX.Element {
  const [language, setLanguage] = useState<string>("en");
  const [message, setMessage] = useState<EmailPreviewMessage>("report_ready");
  const [oneDay, setOneDay] = useState(false);
  const [email, setEmail] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const serial = useRef(0);
  const draftKey = JSON.stringify(fields);

  useEffect(() => {
    const mine = ++serial.current;
    const timer = window.setTimeout(() => {
      setLoading(true);
      previewBrandingEmail(organizationId, language, message, oneDay, fields)
        .then((answer) => {
          if (mine !== serial.current) return;
          setEmail(answer);
          setError(null);
        })
        .catch((err: unknown) => {
          if (mine !== serial.current) return;
          setError(err instanceof Error ? err.message : t("branding.preview_failed"));
        })
        .finally(() => {
          if (mine === serial.current) setLoading(false);
        });
    }, PAGE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // `fields` is compared through its JSON so a re-render with the same draft does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, language, message, oneDay, draftKey]);

  return (
    <Stack gap="xs">
      <Group>
        <Select
          label={t("branding.preview_email_language")}
          data={languages}
          value={language}
          onChange={(v) => setLanguage(v ?? "en")}
          allowDeselect={false}
          w={140}
          data-testid="email-preview-language"
        />
        <Select
          label={t("branding.preview_email_message")}
          data={MESSAGES.map((m) => ({
            value: m.value,
            label: t(m.label),
          }))}
          value={message}
          onChange={(v) => setMessage((v as EmailPreviewMessage | null) ?? "report_ready")}
          allowDeselect={false}
          w={260}
        />
        {message === "pin_reissued" && (
          <Switch
            mt="lg"
            label={t("branding.preview_email_one_day")}
            checked={oneDay}
            onChange={(e) => setOneDay(e.currentTarget.checked)}
          />
        )}
      </Group>
      <Text size="xs" c="dimmed">
        {loading ? t("branding.preview_loading") : t("branding.preview_email_hint")}
      </Text>
      {error !== null && (
        <Alert color="orange" p="xs" data-testid="email-preview-error">
          {error}
        </Alert>
      )}
      {email !== null && (
        <Stack gap={4} data-testid="email-preview">
          <Text size="xs" c="dimmed">
            {t("branding.preview_email_from")} {email.from_display}
          </Text>
          <Text fw={600} data-testid="email-preview-subject">
            {email.subject}
          </Text>
          <iframe
            title={t("branding.preview_email")}
            sandbox=""
            srcDoc={email.html}
            style={{
              width: "100%",
              maxWidth: 640,
              height: 520,
              border: "1px solid #ddd",
              background: "#fff",
            }}
            data-testid="email-preview-frame"
          />
        </Stack>
      )}
    </Stack>
  );
}
